import { makeRequest } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import { PaginationDto } from '@dealstack/shared/lib/common/business/global';
import * as ApiV1 from '@dealstack/shared/lib/common/dataExchange/apiV1';
import { CONTENT_TYPE, HTTP_METHOD } from '@dealstack/shared/lib/common/dataExchange/common';
import { SortingOrder } from '@dealstack/shared/lib/frontend/common-ui/types';
import {
	BulkUpdateInvestorsVestingDto,
	GetDocumentWorkflowFilesRes,
	GetInvestorDetailsWithPersonRes,
	GetInvestorForSendModalRes,
	GetInvestorInvestmentsAndTransactionsRes,
	GetInvestorNamesForFilteringRes,
	GetInvestorsListRes,
	GetInvestorOrderedIdsRes,
	GetInvestorHoldingsRes,
	GetInvestorTransactionDetailsRes,
	GetInvestorJourneysRes,
	GetInvestmentVehicleQuickLinksRes,
	GetInvestorQuickLinksRes,
	GetInvestmentVehicleRelatedPeopleRes,
	GetInvestmentVehicleValuationsRes,
	GetInvestorValuationsRes,
	GetSecurityVestingRes,
	GetKYCRes,
	GetKYCStatisticsRes,
	GetKYCWorkflowsRes,
	KYCMasterFilter,
	GetSelectedInvestorRes,
	InvestmentSecurityRes,
	InvestorInviteRes,
	InvestorsFilterQueryDto,
	QueryRequestDtoV2,
	ReleaseInvestmentWorkflowRes,
	SendInvestorInviteDto,
	AddInvestmentAllocationDto,
	GetInvestmentDetailsAllocationsRes,
	GetInvestmentDetailsBankInformationRes,
	GetInvestmentDetailsKycStatusRes,
	GetInvestmentDetailsLegalDocumentsRes,
	GetInvestmentDetailsOtherDataRes,
	GetManageableLegalDocumentsRes,
	ToggleLegalDocumentDto,
	GetInvestmentDetailsSummaryRes,
	GetInvestmentDetailsTaxComplianceRes,
	SetSelectedBankAccountDto,
	SetInvestmentDetailsPivDto,
	SetTargetDatesDto,
	GetSecurityDataForMipRes,
	InvestmentDetailsAllocation,
	RequestKycDto,
	InvestorBulkActionDto,
	UpdateInvestmentAllocationDto,
	UpdateInvestmentLoanDto,
	AddInvestmentLoanDto,
	BulkClaimOnBehalfPreviewRes,
	BulkClaimOnBehalfRes,
	BulkDownloadFileFilters,
	InvestorKycFormRes,
	AcceptanceStatus,
	UpdateInvestorCustomDetailsDto,
} from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn';
import { ServiceRequestOptions } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';
import { GetInvestorDocumentWorkflowsRes } from '@dealstack/shared/lib/generated/models/GetInvestorDocumentWorkflowsRes';
import { MipFormAttachmentRes } from '@dealstack/shared/lib/generated/models/MipFormAttachmentRes';

export const getDocumentsFilesData = async (
	endpoint: string,
	{
		search,
		limit = 10,
		cursor = '',
		sortingOrder,
	}: {
		search?: string;
		cursor?: string;
		limit?: number;
		sortingOrder?: SortingOrder;
	}
) => {
	return await makeRequest<ApiV1.Response<GetDocumentWorkflowFilesRes>>({
		endpoint,
		method: HTTP_METHOD.GET,
		queryString: {
			search,
			limit: limit?.toString(),
			cursor,
			sortingOrder,
		},
	});
};

export const getInvestorsNamesFiltering = async (
	endpoint: string,
	{
		searchQuery,
		cursor = '',
		limit = 10,
	}: {
		searchQuery?: string;
		cursor?: string;
		limit?: number;
	}
) => {
	return await makeRequest<ApiV1.Response<GetInvestorNamesForFilteringRes>>({
		endpoint,
		method: HTTP_METHOD.GET,
		queryString: {
			searchQuery,
			cursor,
			limit: limit?.toString(),
		},
	});
};

export const getMIPInvestorsData = async (
	endpoint: string,
	args: QueryRequestDtoV2,
	signal?: AbortSignal
) => {
	const { offset, limit, filterQuery, sortingQuery, searchQuery } = args;
	return await makeRequest<ApiV1.Response<GetInvestorsListRes>>(
		{
			endpoint,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({
				limit,
				offset,
				filterQuery,
				sortingQuery,
				searchQuery,
			}),
		},
		{ abortSignal: signal }
	);
};

export const getMIPInvestorsOrderedIds = async (
	endpoint: string,
	args: Pick<QueryRequestDtoV2, 'filterQuery' | 'sortingQuery' | 'searchQuery'>,
	signal?: AbortSignal
) => {
	const { filterQuery, sortingQuery, searchQuery } = args;
	return await makeRequest<ApiV1.Response<GetInvestorOrderedIdsRes>>(
		{
			endpoint,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({ filterQuery, sortingQuery, searchQuery }),
		},
		{ abortSignal: signal }
	);
};

// The MIP's KYC forms + which are attached to a KYC workflow (drawer toggle).
export const getMipFormsWithAttachment = async (params: {
	dealId: string;
	programmeId: string;
	kycWorkflowId: string;
}) => {
	return await makeRequest<ApiV1.Response<MipFormAttachmentRes[]>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_GET,
			{
				dealId: params.dealId,
				programmeId: params.programmeId,
				kycWorkflowId: params.kycWorkflowId,
			}
		),
		method: HTTP_METHOD.GET,
	});
};

export const getInvestorFormsForReview = async (params: {
	dealId: string;
	programmeId: string;
	kycWorkflowId: string;
}) => {
	return await makeRequest<ApiV1.Response<InvestorKycFormRes[]>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_INVESTOR_FORMS_GET,
			{
				dealId: params.dealId,
				programmeId: params.programmeId,
				kycWorkflowId: params.kycWorkflowId,
			}
		),
		method: HTTP_METHOD.GET,
	});
};

export const reviewKycForm = async (params: {
	dealId: string;
	programmeId: string;
	kycWorkflowId: string;
	templateId: string;
	status: AcceptanceStatus;
	rejectionComment?: string;
}) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_REVIEW_PUT,
			{
				dealId: params.dealId,
				programmeId: params.programmeId,
				kycWorkflowId: params.kycWorkflowId,
			}
		),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({
			templateId: params.templateId,
			status: params.status,
			rejectionComment: params.rejectionComment,
		}),
	});
};

export const resetKycForm = async (params: {
	dealId: string;
	programmeId: string;
	kycWorkflowId: string;
	templateId: string;
}) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_RESET_PUT,
			{
				dealId: params.dealId,
				programmeId: params.programmeId,
				kycWorkflowId: params.kycWorkflowId,
			}
		),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify({ templateId: params.templateId }),
	});
};

// Per-workflow override: add / remove a KYC form on a KYC workflow.
export const attachKycFormToKycWorkflow = async (params: {
	dealId: string;
	programmeId: string;
	kycWorkflowId: string;
	templateId: string;
}) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_POST,
			{
				dealId: params.dealId,
				programmeId: params.programmeId,
				kycWorkflowId: params.kycWorkflowId,
			}
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({ templateId: params.templateId }),
	});
};

export const removeKycFormFromKycWorkflow = async (params: {
	dealId: string;
	programmeId: string;
	kycFormWorkflowStateId: string;
}) => {
	return await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYCFORMWORKFLOWSTATEID_DELETE,
			{
				dealId: params.dealId,
				programmeId: params.programmeId,
				kycFormWorkflowStateId: params.kycFormWorkflowStateId,
			}
		),
		method: HTTP_METHOD.DELETE,
	});
};

export const getMIPSelectedInvestorsData = async (
	endpoint: string,
	args: InvestorBulkActionDto,
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetSelectedInvestorRes>>(
		{
			endpoint,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ abortSignal: signal }
	);
};

export const getMIPInvestorsDataForSendModal = async (
	endpoint: string,
	args: InvestorBulkActionDto,
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetInvestorForSendModalRes>>(
		{
			endpoint,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ abortSignal: signal }
	);
};

export const getMIPInvestorsIdsData = async (
	endpoint: string,
	args: InvestorsFilterQueryDto,
	signal?: AbortSignal
) => {
	const { filterQuery } = args;
	return await makeRequest<ApiV1.Response<string[]>>(
		{
			endpoint,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({
				filterQuery,
			}),
		},
		{ abortSignal: signal }
	);
};

export const getMIPInvestorDetailsData = async (
	endpoint: string,
	args: { investorId?: string },
	signal?: AbortSignal
) => {
	const { investorId } = args;
	if (!investorId) {
		return;
	}
	return await makeRequest<ApiV1.Response<GetInvestorDetailsWithPersonRes>>(
		{ endpoint: formatUnicorn(endpoint, { id: investorId }), method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getInvestorInvestmentsAndTransactions = async (
	args: { investorId: string },
	signal?: AbortSignal
) => {
	const { investorId } = args;

	return await makeRequest<ApiV1.Response<GetInvestorInvestmentsAndTransactionsRes>>(
		{
			endpoint: formatUnicorn(
				API_PATHS.MIP_INVESTOR_GET_INVESTMENTS_TRANSACTIONS_INVESTORID_GET,
				{ investorId }
			),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getMIPInvestorHoldingsData = async (
	endpoint: string,
	args: { investorId: string },
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetInvestorHoldingsRes>>(
		{ endpoint: formatUnicorn(endpoint, { id: args.investorId }), method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getInvestorHoldingTransactions = async (
	endpoint: string,
	args: { investorId: string; holderId: string; securityId: string; programmeId?: string },
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetInvestorTransactionDetailsRes[]>>(
		{
			endpoint: formatUnicorn(endpoint, { id: args.investorId }),
			method: HTTP_METHOD.GET,
			queryString: {
				holderId: args.holderId,
				securityId: args.securityId,
				programmeId: args.programmeId,
			},
		},
		{ abortSignal: signal }
	);
};

export const getInvestmentVehicleHoldings = async (
	endpoint: string,
	args: { personId: string },
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetInvestorHoldingsRes>>(
		{ endpoint: formatUnicorn(endpoint, { personId: args.personId }), method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getInvestmentVehicleRelatedPeople = async (
	endpoint: string,
	args: { personId: string },
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetInvestmentVehicleRelatedPeopleRes>>(
		{ endpoint: formatUnicorn(endpoint, { personId: args.personId }), method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getInvestmentVehicleValuations = async (
	endpoint: string,
	args: { personId: string },
	signal?: AbortSignal
) => {
	const { personId } = args;
	return await makeRequest<ApiV1.Response<GetInvestmentVehicleValuationsRes>>(
		{ endpoint: formatUnicorn(endpoint, { personId }), method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getMIPInvestorInvestmentValuationsData = async (
	endpoint: string,
	args: { investorId?: string },
	signal?: AbortSignal
) => {
	const { investorId } = args;
	if (!investorId) {
		return;
	}
	return await makeRequest<ApiV1.Response<GetInvestorValuationsRes>>(
		{ endpoint: formatUnicorn(endpoint, { id: investorId }), method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getMIPInvestorJourneysData = async (
	endpoint: string,
	args: { investorId: string },
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetInvestorJourneysRes>>(
		{ endpoint: formatUnicorn(endpoint, { id: args.investorId }), method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getMIPInvestorQuickLinksData = async (
	endpoint: string,
	args: { investorId: string },
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetInvestorQuickLinksRes>>(
		{ endpoint: formatUnicorn(endpoint, { id: args.investorId }), method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getInvestmentVehicleQuickLinks = async (
	endpoint: string,
	args: { personId: string },
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<GetInvestmentVehicleQuickLinksRes>>(
		{ endpoint: formatUnicorn(endpoint, { personId: args.personId }), method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getSecurityVestingData = async (
	endpoint: string,
	args: { investorId?: string; securityId?: string },
	signal?: AbortSignal
) => {
	const { investorId, securityId } = args;
	if (!investorId || !securityId) {
		return;
	}
	return await makeRequest<ApiV1.Response<GetSecurityVestingRes>>(
		{
			endpoint: formatUnicorn(endpoint, { investorId, securityId }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getMIPInvestorDocumentWorkflowListData = async (
	endpoint: string,
	args: { investorId?: string },
	signal?: AbortSignal
) => {
	const { investorId } = args;
	if (!investorId) {
		return;
	}
	return await makeRequest<ApiV1.Response<GetInvestorDocumentWorkflowsRes>>(
		{
			endpoint: formatUnicorn(endpoint, { investorId }),
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getMIPInvestorContractListData = async (
	endpoint: string,
	args: { investorId?: string },
	signal?: AbortSignal
) => {
	const { investorId } = args;
	if (!investorId) {
		return;
	}
	return await makeRequest<ApiV1.Response<unknown>>(
		{ endpoint: formatUnicorn(endpoint, { id: investorId }), method: HTTP_METHOD.GET },
		{ abortSignal: signal }
	);
};

export const getMipKYCData = async (
	endpoint: string,
	args: PaginationDto,
	signal?: AbortSignal
) => {
	const { searchQuery, limit, offset } = args;
	return await makeRequest<ApiV1.Response<GetKYCRes>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
			queryString: {
				searchQuery,
				limit,
				offset,
			},
		},
		{ abortSignal: signal }
	);
};

export const getMIPInvestorKycData = async (
	endpoint: string,
	args: { investorId?: string; kycWorkflowId?: string },
	signal?: AbortSignal
) => {
	const { investorId, kycWorkflowId } = args;
	if (!investorId) {
		return;
	}
	return await makeRequest<ApiV1.Response<GetKYCRes>>(
		{
			endpoint: formatUnicorn(endpoint, { investorId }),
			method: HTTP_METHOD.GET,
			queryString: kycWorkflowId ? { kycWorkflowId } : undefined,
		},
		{ abortSignal: signal }
	);
};

export const getMipKYCStatisticsData = async (endpoint: string, signal?: AbortSignal) => {
	return await makeRequest<ApiV1.Response<GetKYCStatisticsRes>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
		},
		{ abortSignal: signal }
	);
};

export const getMipKYCWorkflows = async (
	endpoint: string,
	args: {
		searchQuery?: string;
		status?: string[];
		offset?: number;
		limit?: number;
		declarationsStatus?: string[];
		proofOfIdentityStatus?: string[];
		proofOfAddressStatus?: string[];
		investmentVehicleStatus?: string[];
		formsStatus?: string[];
		sortBy?: string;
		sortDesc?: boolean;
		investmentType?: string[];
		masterFilter?: KYCMasterFilter;
		cohortIds?: string[];
		investorIds?: string[];
	} = {},
	signal?: AbortSignal
) => {
	const {
		searchQuery,
		status,
		offset,
		limit,
		declarationsStatus,
		proofOfIdentityStatus,
		proofOfAddressStatus,
		investmentVehicleStatus,
		formsStatus,
		sortBy,
		sortDesc,
		investmentType,
		masterFilter,
		cohortIds,
		investorIds,
	} = args;
	return await makeRequest<ApiV1.Response<GetKYCWorkflowsRes>>(
		{
			endpoint,
			method: HTTP_METHOD.GET,
			queryString: {
				searchQuery,
				status,
				offset,
				limit,
				declarationsStatus,
				proofOfIdentityStatus,
				proofOfAddressStatus,
				investmentVehicleStatus,
				formsStatus,
				sortBy,
				sortDesc,
				investmentType,
				masterFilter,
				cohortIds,
				investorIds,
			},
		},
		{ abortSignal: signal }
	);
};

export const deleteMipStandaloneKycWorkflow = async (kycWorkflowId: string) => {
	await makeRequest<void>({
		endpoint: formatUnicorn(API_PATHS.MIP_KYC_WORKFLOWS_STANDALONE_KYCWORKFLOWID_DELETE, {
			kycWorkflowId,
		}),
		method: HTTP_METHOD.DELETE,
	});
};

export const postMipInvestorsInvitations = async (
	args: SendInvestorInviteDto,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<InvestorInviteRes>>(
		{
			endpoint: API_PATHS.MIP_INVESTOR_INVITE_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ ...options }
	);
	return response;
};
export const postMipRequestKyc = async (args: RequestKycDto, options?: ServiceRequestOptions) => {
	const response = await makeRequest<ApiV1.Response<{ sentCount: number; skippedCount: number }>>(
		{
			endpoint: API_PATHS.MIP_INVESTOR_REQUEST_KYC_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{ ...options }
	);
	return response;
};

export type KycBulkDownloadFilters = Record<string, unknown>;

export type KycBulkDownloadArgs = {
	allSelected: boolean;
	selectedIds?: string[];
	excludedIds?: string[];
	filters?: KycBulkDownloadFilters;
};

export const postMipKYCWorkflowsDownloadFiles = async (
	args: KycBulkDownloadArgs,
	options?: ServiceRequestOptions
) => {
	const response = await makeRequest<ApiV1.Response<{ isAsync: boolean; taskId: string }>>(
		{
			endpoint: API_PATHS.MIP_KYC_BULK_DOWNLOAD_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify({
				allSelected: args.allSelected,
				selectedIds: args.selectedIds,
				excludedIds: args.excludedIds,
				...args.filters,
			}),
		},
		{ ...options }
	);
	return response;
};

export const bulkReleaseInvestmentWorkflow = async (
	args: { mipId: string } & InvestorBulkActionDto,
	options?: ServiceRequestOptions
) => {
	const { mipId, ...body } = args;
	const response = await makeRequest<ApiV1.Response<ReleaseInvestmentWorkflowRes>>(
		{
			endpoint: formatUnicorn(
				API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_BULK_RELEASE_INVESTMENT_WORKFLOW_POST,
				{ mipId }
			),
			method: HTTP_METHOD.POST,
			body: JSON.stringify(body),
		},
		{ ...options }
	);
	return response;
};

export const postMipInvestorsReleaseSingle = async (args: {
	mipId: string;
	investorId: string;
}) => {
	const response = await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_RELEASE_SINGLE_INVESTMENT_WORKFLOW_POST,
			{ mipId: args.mipId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify({
			investorId: args.investorId,
		}),
	});
	return response;
};

export const downloadInvestors = async (args: InvestorBulkActionDto) => {
	const response = await makeRequest<Blob>({
		endpoint: API_PATHS.MIP_INVESTOR_BULK_DOWNLOAD_POST,
		method: HTTP_METHOD.POST,
		customHeaders: {
			Accept: CONTENT_TYPE.ZIP,
		},
		body: JSON.stringify(args),
	});
	return response;
};

export const getMIPInvestorSecuritiesList = async (
	args: InvestorBulkActionDto,
	signal?: AbortSignal
) => {
	return await makeRequest<ApiV1.Response<InvestmentSecurityRes[]>>(
		{
			endpoint: API_PATHS.MIP_INVESTOR_SECURITIES_LIST_POST,
			method: HTTP_METHOD.POST,
			body: JSON.stringify(args),
		},
		{
			abortSignal: signal,
		}
	);
};

export const bulkUpdateInvestorVestingTerms = async (
	args: BulkUpdateInvestorsVestingDto
): Promise<void> => {
	await makeRequest({
		endpoint: API_PATHS.MIP_VESTING_TERM_INVESTORS_BULK_UPDATE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify(args),
	});
};

export const createDownloadWorkflow = async (args: InvestorBulkActionDto): Promise<void> => {
	await makeRequest({
		endpoint: API_PATHS.MIP_INVESTOR_BULK_GENERATE_FILE_POST,
		method: HTTP_METHOD.POST,
		customHeaders: {
			Accept: CONTENT_TYPE.JSON,
		},
		body: JSON.stringify(args),
	});
};

export const startInvestmentWorkflow = async ({
	mipId,
	investmentId,
}: {
	mipId: string;
	investmentId: string;
}) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsSummaryRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_START_POST,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.POST,
	});
};

export const approveInvestmentWorkflow = async ({
	mipId,
	investmentId,
}: {
	mipId: string;
	investmentId: string;
}) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsSummaryRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_APPROVE_POST,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.POST,
	});
};

export const makeDraftInvestmentWorkflow = async ({
	mipId,
	investmentId,
}: {
	mipId: string;
	investmentId: string;
}) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsSummaryRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_MAKE_DRAFT_POST,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.POST,
	});
};

export const setInvestmentTargetDates = async ({
	mipId,
	investmentId,
	payload,
}: {
	mipId: string;
	investmentId: string;
	payload: SetTargetDatesDto;
}) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsSummaryRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_TARGET_DATES_POST,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(payload),
	});
};

export const setInvestmentDetailsPiv = async ({
	mipId,
	investmentId,
	payload,
}: {
	mipId: string;
	investmentId: string;
	payload: SetInvestmentDetailsPivDto;
}) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsSummaryRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_PIV_POST,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(payload),
	});
};

export const setInvestmentSelectedBankAccount = async ({
	mipId,
	investmentId,
	payload,
}: {
	mipId: string;
	investmentId: string;
	payload: SetSelectedBankAccountDto;
}) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsBankInformationRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_BANK_ACCOUNT_POST,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(payload),
	});
};

export const revokeInvestmentWorkflow = async (revokeInvestmentWorkflowId: string) => {
	return await makeRequest({
		endpoint: formatUnicorn(API_PATHS.MIP_INVESTOR_REVOKE_INVESTMENTWORKFLOWID_POST, {
			investmentWorkflowId: revokeInvestmentWorkflowId,
		}),
		method: HTTP_METHOD.POST,
	});
};

export const bulkRevokeInvestmentWorkflow = async (investmentWorkflowIds: string[]) => {
	return await makeRequest({
		endpoint: API_PATHS.MIP_INVESTOR_BULK_REVOKE_POST,
		method: HTTP_METHOD.POST,
		body: JSON.stringify({ investmentWorkflowIds }),
	});
};

export const removeInvestorFromMip = async (investorId: string) => {
	return await makeRequest({
		endpoint: formatUnicorn(API_PATHS.MIP_INVESTOR_REMOVE_INVESTOR_INVESTORID_POST, {
			investorId,
		}),
		method: HTTP_METHOD.POST,
	});
};

export const claimSignerOnBehalf = async ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	signerId,
}: {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	signerId: string;
}) => {
	await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_DOCUMENTWORKFLOWINSTANCEID_SIGNER_SIGNERID_CLAIM_POST,
			{
				dealId,
				programmeId,
				documentWorkflowInstanceId,
				signerId,
			}
		),
		method: HTTP_METHOD.POST,
	});
};

export interface BulkClaimSelectionBody {
	allDocumentsSelected: boolean;
	selectedDocumentWorkflowInstanceIds?: string[];
	excludedDocumentWorkflowInstanceIds?: string[];
	filters?: BulkDownloadFileFilters;
}

export const bulkClaimOnBehalfPreview = async ({
	dealId,
	programmeId,
	body,
}: {
	dealId: string;
	programmeId: string;
	body: BulkClaimSelectionBody;
}) =>
	await makeRequest<ApiV1.Response<BulkClaimOnBehalfPreviewRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_BULK_CLAIM_ON_BEHALF_PREVIEW_POST,
			{ dealId, programmeId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});

export const bulkClaimOnBehalf = async ({
	dealId,
	programmeId,
	body,
}: {
	dealId: string;
	programmeId: string;
	body: { signerIds: string[] };
}) =>
	await makeRequest<ApiV1.Response<BulkClaimOnBehalfRes>>({
		endpoint: formatUnicorn(
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_BULK_CLAIM_ON_BEHALF_POST,
			{ dealId, programmeId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(body),
	});

export const getInvestmentDetailsSummary = async (mipId: string, investmentId: string) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsSummaryRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_SUMMARY_GET,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.GET,
	});
};

export const getInvestmentDetailsAllocations = async (mipId: string, investmentId: string) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsAllocationsRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_ALLOCATIONS_GET,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.GET,
	});
};

export const getInvestmentDetailsLegalDocuments = async (mipId: string, investmentId: string) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsLegalDocumentsRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_LEGAL_DOCUMENTS_GET,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.GET,
	});
};

export const getManageableLegalDocuments = async (mipId: string, investmentId: string) => {
	return await makeRequest<ApiV1.Response<GetManageableLegalDocumentsRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_LEGAL_DOCUMENTS_MANAGEABLE_GET,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.GET,
	});
};

export const toggleLegalDocument = async ({
	mipId,
	investmentId,
	templateId,
	payload,
}: {
	mipId: string;
	investmentId: string;
	templateId: string;
	payload: ToggleLegalDocumentDto;
}) => {
	return await makeRequest<ApiV1.Response<GetManageableLegalDocumentsRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_LEGAL_DOCUMENTS_MANAGEABLE_TEMPLATEID_PUT,
			{ mipId, investmentId, templateId }
		),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(payload),
	});
};

export const getInvestmentDetailsBankInformation = async (mipId: string, investmentId: string) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsBankInformationRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_BANK_INFORMATION_GET,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.GET,
	});
};

export const addInvestmentAllocation = async (params: {
	mipId: string;
	investmentId: string;
	payload: AddInvestmentAllocationDto;
}) => {
	const { mipId, investmentId, payload } = params;

	return makeRequest<ApiV1.Response<InvestmentDetailsAllocation>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_ALLOCATIONS_POST,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(payload),
	}).then((response) => response.payload);
};

export const updateInvestmentAllocation = async (params: {
	mipId: string;
	investmentId: string;
	allocationId: string;
	payload: UpdateInvestmentAllocationDto;
}) => {
	const { mipId, investmentId, allocationId, payload } = params;

	return makeRequest<ApiV1.Response<InvestmentDetailsAllocation>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_ALLOCATIONS_ALLOCATIONID_PATCH,
			{ mipId, investmentId, allocationId }
		),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(payload),
	}).then((response) => response.payload);
};

export const removeInvestmentAllocation = async (params: {
	mipId: string;
	investmentId: string;
	allocationId: string;
}) => {
	const { mipId, investmentId, allocationId } = params;

	return makeRequest<ApiV1.Response<{ id: string }>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_ALLOCATIONS_ALLOCATIONID_DELETE,
			{ mipId, investmentId, allocationId }
		),
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};

export const addInvestmentLoan = async (params: {
	mipId: string;
	investmentId: string;
	payload: AddInvestmentLoanDto;
}) => {
	const { mipId, investmentId, payload } = params;

	return await makeRequest<ApiV1.Response<GetInvestmentDetailsSummaryRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_LOAN_POST,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.POST,
		body: JSON.stringify(payload),
	});
};

export const updateInvestmentLoan = async (params: {
	mipId: string;
	investmentId: string;
	loanId: string;
	payload: UpdateInvestmentLoanDto;
}) => {
	const { mipId, investmentId, loanId, payload } = params;

	return await makeRequest<ApiV1.Response<GetInvestmentDetailsSummaryRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_LOAN_LOANID_PATCH,
			{ mipId, investmentId, loanId }
		),
		method: HTTP_METHOD.PATCH,
		body: JSON.stringify(payload),
	});
};

export const removeInvestmentLoan = async (params: {
	mipId: string;
	investmentId: string;
	loanId: string;
}) => {
	const { mipId, investmentId, loanId } = params;

	return await makeRequest<ApiV1.Response<{ id: string }>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_LOAN_LOANID_DELETE,
			{ mipId, investmentId, loanId }
		),
		method: HTTP_METHOD.DELETE,
	}).then((response) => response.payload);
};

export const getMipSecurities = async (mipId: string) => {
	return await makeRequest<ApiV1.Response<GetSecurityDataForMipRes[]>>({
		endpoint: formatUnicorn(API_PATHS.SECURITY_MIP_SECURITIES_MIPID_GET, { mipId }),
		method: HTTP_METHOD.GET,
	});
};

export const getInvestmentDetailsOtherData = async (mipId: string, investmentId: string) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsOtherDataRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_OTHER_DATA_GET,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.GET,
	});
};

export const getInvestmentDetailsTaxCompliance = async (mipId: string, investmentId: string) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsTaxComplianceRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_TAX_COMPLIANCE_GET,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.GET,
	});
};

export const getInvestmentDetailsKycStatus = async (mipId: string, investmentId: string) => {
	return await makeRequest<ApiV1.Response<GetInvestmentDetailsKycStatusRes>>({
		endpoint: formatUnicorn(
			API_PATHS.MIP_MIPID_INVESTOR_INVESTMENT_WORKFLOW_INVESTMENT_DETAILS_INVESTMENTID_KYC_STATUS_GET,
			{ mipId, investmentId }
		),
		method: HTTP_METHOD.GET,
	});
};

export const updateInvestorCustomDetails = async ({
	investorId,
	payload,
}: {
	investorId: string;
	payload: UpdateInvestorCustomDetailsDto;
}) =>
	await makeRequest<ApiV1.Response<void>>({
		endpoint: formatUnicorn(API_PATHS.MIP_INVESTOR_INVESTORID_CUSTOM_DETAILS_PUT, {
			investorId,
		}),
		method: HTTP_METHOD.PUT,
		body: JSON.stringify(payload),
	});
